import React, { useState } from 'react';
import { Modal, Table, Badge, Button } from 'react-bootstrap';
import './CommandManualModal.css';

interface CommandManualModalProps {
  show: boolean;
  onHide: () => void;
}

interface CommandInfo {
  command: string;
  description: string;
  example: string;
  category: string;
}

const COMMANDS: CommandInfo[] = [
  {
    command: 'addCreature',
    description: '添加生物到游戏中',
    example: 'addCreature player1 ally mig=8 maxHp=100 wlp=12 def=1 mDef=2 fixedDef=1',
    category: '生物管理'
  },
  {
    command: 'removeCreature',
    description: '从游戏中移除生物',
    example: 'removeCreature player1',
    category: '生物管理'
  },
  {
    command: 'hp',
    description: '修改生物的生命值',
    example: 'hp player1 -10',
    category: '属性修改'
  },
  {
    command: 'mp',
    description: '修改生物的魔法值',
    example: 'mp player1 +20',
    category: '属性修改'
  },
  {
    command: 'ip',
    description: '修改生物的物资点（仅限盟友，敌人没有 IP）',
    example: 'ip player1 +1',
    category: '属性修改'
  },
  {
    command: 'status',
    description: '添加或移除状态效果（6 种固定状态之一）',
    example: 'status player1 +poisoned -slow',
    category: '状态管理'
  },
  {
    command: 'buff',
    description: '添加或移除增益/减益（自由文本，或 defFixedN / mDefFixedN 固定防御值）',
    example: 'buff player1 +defFixed12 -命中加成',
    category: '状态管理'
  },
  {
    command: 'study',
    description: '设置敌人的研究等级',
    example: 'study enemy1 7+',
    category: '状态管理'
  },
  {
    command: 'clearAll',
    description: '清除所有生物数据',
    example: 'clearAll',
    category: '系统管理'
  }
];

const CATEGORIES = ['生物管理', '属性修改', '状态管理', '系统管理'];

export const CommandManualModal: React.FC<CommandManualModalProps> = ({ show, onHide }) => {
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null);

  const getCommandsByCategory = (category: string) => {
    return COMMANDS.filter(cmd => cmd.category === category);
  };

  const handleCopyCommand = async (command: string) => {
    try {
      await navigator.clipboard.writeText(command);
      setCopiedCommand(command);
      setTimeout(() => setCopiedCommand(null), 2000); // 2秒后清除提示
    } catch (err) {
      console.error('复制失败:', err);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" dialogClassName="command-manual-modal">
      <Modal.Header closeButton>
        <Modal.Title>命令手册</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="command-manual-content">
          <div className="manual-intro">
            <p>使用以下命令来控制游戏。在命令面板中输入命令并按 Enter 执行。</p>
            <p><strong>快捷键：</strong>按 Enter 打开命令面板，按 Escape 关闭。</p>
          </div>

          {CATEGORIES.map(category => (
            <div key={category} className="command-category">
              <h4>
                <Badge bg="primary">{category}</Badge>
              </h4>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th style={{ width: '20%' }}>命令</th>
                    <th style={{ width: '30%' }}>描述</th>
                    <th style={{ width: '50%' }}>示例</th>
                  </tr>
                </thead>
                <tbody>
                  {getCommandsByCategory(category).map((cmd, index) => (
                    <tr key={index}>
                      <td><code>{cmd.command}</code></td>
                      <td>{cmd.description}</td>
                      <td>
                        <div className="example-container">
                          <code className="example-code">{cmd.example}</code>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="copy-button"
                            onClick={() => handleCopyCommand(cmd.example)}
                            title="复制到剪贴板"
                          >
                            {copiedCommand === cmd.example ? '✓' : '📋'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ))}

          <div className="command-tips">
            <h4>使用提示</h4>
            <ul>
              <li><strong>addCreature 属性说明：</strong></li>
              <ul className="attribute-list">
                <li><code>maxHp</code> - 最大生命值</li>
                <li><code>maxMp</code> - 最大魔法值</li>
                <li><code>maxIp</code> - 最大物资点（仅盟友有意义，默认盟友 6 / 敌人 0）</li>
                <li><code>dex</code> - 敏捷（骰子大小）</li>
                <li><code>ins</code> - 洞察（骰子大小）</li>
                <li><code>mig</code> - 力量（骰子大小）</li>
                <li><code>wlp</code> - 意志（骰子大小）</li>
                <li><code>def</code> - 物理防御修正</li>
                <li><code>mDef</code> - 魔法防御修正</li>
                <li><code>fixedDef</code> - 固定防御（1=是，0=否）</li>
              </ul>
              <li>属性参数使用 <code>key=value</code> 格式（如：<code>maxHp=100</code>）</li>
              <li>状态效果使用 <code>+</code> 添加，<code>-</code> 移除（如：<code>+poisoned -slow</code>）；6 种固定状态：<code>weak</code>、<code>slow</code>、<code>enraged</code>、<code>dazed</code>、<code>poisoned</code>、<code>shaken</code></li>
              <li>增益/减益（<code>buff</code>）同样用 <code>+</code>/<code>-</code>；名字可以是任意自由文本，也可以是有机制效果的固定值写法 <code>defFixedN</code>/<code>mDefFixedN</code>（N 为具体数值）</li>
              <li>HP/MP/IP 修改使用正负数（如：<code>hp player1 -10</code>）</li>
              <li>多个命令可以用换行符分隔</li>
              <li>研究等级选项：<code>7+</code>, <code>10+</code>, <code>13+</code>, <code>null</code></li>
            </ul>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="text-muted">
          <small>按 Enter 打开命令面板开始使用</small>
        </div>
      </Modal.Footer>
    </Modal>
  );
}; 